import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, Typography, MenuItem } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createBlog, updateBlog, getBlogById } from '../services/blogService';
import api from '../services/api'; // Import the API instance
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import MediaUploadField from './MediaUploadField';

// Quill toolbar configuration
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }], // Headings
  ['bold', 'italic', 'underline', 'strike'], // Text styling
  [{ color: [] }, { background: [] }], // Font and background colors
  [{ script: 'sub' }, { script: 'super' }], // Subscript / Superscript
  [{ font: [] }], // Font family
  [{ size: [] }], // Font size
  [{ align: [] }], // Text alignment
  [{ list: 'ordered' }, { list: 'bullet' }], // Lists
  [{ indent: '-1' }, { indent: '+1' }], // Indentation
  ['blockquote', 'code-block'], // Blockquote and code block
  ['link', 'image', 'video'], // Links, images, videos
  ['clean'], // Remove formatting
];

const modules = {
  toolbar: toolbarOptions,
};

function BlogForm() {
  const [authors, setAuthors] = useState([]);
  const [initialValues, setInitialValues] = useState({
    title: '',
    titleAr: '',
    content: '',
    contentAr: '',
    metaDescription: '',
    metaDescriptionAr: '',
    metaTitle: '',
    metaTitleAr: '',
    category: '',
    categoryAr: '',
    imageUrl: '',
    altText: '',
    slug: '',
    authorId: '',
    keywords: '', // New SEO field
    publishDate: new Date().toISOString().split("T")[0], // Default to today's date
  });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthors();
    if (id) fetchBlog();
  }, [id]);

  const fetchAuthors = async () => {
    try {
      const response = await api.get('/authors');
      setAuthors(response.data || []);
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const fetchBlog = async () => {
    try {
      const data = await getBlogById(id);
      setInitialValues({
        title: data.title,
        titleAr: data.titleAr || '',
        content: data.content,
        contentAr: data.contentAr || '',
        metaDescription: data.metaDescription,
        metaDescriptionAr: data.metaDescriptionAr || '',
        metaTitle: data.metaTitle,
        metaTitleAr: data.metaTitleAr || '',
        category: data.category,
        categoryAr: data.categoryAr || '',
        imageUrl: data.imageUrl,
        altText: data.altText,
        slug: data.slug,
        authorId: data.author?.authorId || '',
        keywords: data.keywords || '',
        publishDate: data.publishDate ? data.publishDate.split("T")[0] : new Date().toISOString().split("T")[0],
      });
    } catch (error) {
      console.error('Error fetching blog:', error);
    }
  };

  const BlogSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    content: Yup.string().required('Content is required'),
    metaDescription: Yup.string().max(300, 'Meta description should not exceed 300 characters'),
    metaTitle: Yup.string().max(150, 'Meta title should not exceed 150 characters'),
    category: Yup.string().required('Category is required'),
    keywords: Yup.string().required('Keywords are required'),
    altText: Yup.string().required('Alt text is required for the image'),
    slug: Yup.string()
      .required('Slug is required')
      .matches(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
      .min(3, 'Slug must be at least 3 characters')
      .max(100, 'Slug must not exceed 100 characters'),
    publishDate: Yup.date().required('Publish date is required'),
    authorId: Yup.string().required('Author is required'),
    // Arabic fields are optional
    titleAr: Yup.string().max(150, 'Arabic title should not exceed 150 characters'),
    metaDescriptionAr: Yup.string().max(300, 'Arabic meta description should not exceed 300 characters'),
    metaTitleAr: Yup.string().max(150, 'Arabic meta title should not exceed 150 characters'),
    categoryAr: Yup.string().max(100, 'Arabic category should not exceed 100 characters'),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateBlog(id, values);
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Add 2-second delay before sending the request
        await createBlog(values);
      }
      navigate('/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Blog Post' : 'Create Blog Post'}
          </Typography>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={BlogSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* English Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  English Content
                </Typography>
                
                <TextField
                  label="Title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                />
                
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Content
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.content}
                    onChange={(value) => setFieldValue('content', value)}
                    modules={modules}
                  />
                  {touched.content && errors.content && (
                    <Typography color="error">{errors.content}</Typography>
                  )}
                </Box>

                {/* Arabic Fields */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'secondary.main' }}>
                  Arabic Content (Optional)
                </Typography>
                
                <TextField
                  label="Arabic Title"
                  name="titleAr"
                  value={values.titleAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  error={touched.titleAr && Boolean(errors.titleAr)}
                  helperText={touched.titleAr && errors.titleAr}
                  inputProps={{ dir: 'rtl' }}
                />
                
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Arabic Content
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.contentAr}
                    onChange={(value) => setFieldValue('contentAr', value)}
                    modules={modules}
                  />
                </Box>

                {/* Common Fields */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                  General Information
                </Typography>

                <TextField
                  select
                  label="Author"
                  name="authorId"
                  value={values.authorId}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.authorId && Boolean(errors.authorId)}
                  helperText={touched.authorId && errors.authorId}
                >
                  {authors.map((author) => (
                    <MenuItem key={author.authorId} value={author.authorId}>
                      {author.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Category"
                    name="category"
                    value={values.category}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    error={touched.category && Boolean(errors.category)}
                    helperText={touched.category && errors.category}
                  />
                  <TextField
                    label="Arabic Category"
                    name="categoryAr"
                    value={values.categoryAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.categoryAr && Boolean(errors.categoryAr)}
                    helperText={touched.categoryAr && errors.categoryAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                {/* SEO Fields */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                  SEO Information
                </Typography>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Meta Title"
                    name="metaTitle"
                    value={values.metaTitle}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.metaTitle && Boolean(errors.metaTitle)}
                    helperText={touched.metaTitle && errors.metaTitle}
                  />
                  <TextField
                    label="Arabic Meta Title"
                    name="metaTitleAr"
                    value={values.metaTitleAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.metaTitleAr && Boolean(errors.metaTitleAr)}
                    helperText={touched.metaTitleAr && errors.metaTitleAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Meta Description"
                    name="metaDescription"
                    value={values.metaDescription}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={3}
                    error={touched.metaDescription && Boolean(errors.metaDescription)}
                    helperText={touched.metaDescription && errors.metaDescription}
                  />
                  <TextField
                    label="Arabic Meta Description"
                    name="metaDescriptionAr"
                    value={values.metaDescriptionAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={3}
                    error={touched.metaDescriptionAr && Boolean(errors.metaDescriptionAr)}
                    helperText={touched.metaDescriptionAr && errors.metaDescriptionAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <TextField
                  label="Keywords"
                  name="keywords"
                  value={values.keywords}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.keywords && Boolean(errors.keywords)}
                  helperText={touched.keywords && errors.keywords}
                />
                
                <TextField
                  label="Slug"
                  name="slug"
                  value={values.slug}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.slug && Boolean(errors.slug)}
                  helperText={touched.slug && errors.slug}
                />
                
                <MediaUploadField
                  label="Blog Image"
                  value={values.imageUrl}
                  onChange={(nextValue) => setFieldValue('imageUrl', nextValue)}
                  kind="image"
                  scope="blogs"
                  slug={values.slug}
                  error={errors.imageUrl}
                  touched={touched.imageUrl}
                />
                
                <TextField
                  label="Alt Text"
                  name="altText"
                  value={values.altText}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.altText && Boolean(errors.altText)}
                  helperText={touched.altText && errors.altText}
                />
                
                <TextField
                  label="Publish Date"
                  name="publishDate"
                  type="date"
                  value={values.publishDate}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.publishDate && Boolean(errors.publishDate)}
                  helperText={touched.publishDate && errors.publishDate}
                  InputLabelProps={{ shrink: true }}
                />
                
                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {id ? 'Update Blog Post' : 'Create Blog Post'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default BlogForm;
